import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ProductRow } from '../api';

export function ProductsPage() {
  const [q, setQ] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['products', q],
    queryFn: () => api.products({ q, limit: 100 }),
  });

  return (
    <>
      <div className="page-header">
        <h2>Товары</h2>
        <input
          className="search"
          placeholder="Поиск по названию или артикулу"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {isLoading && <div className="card">Загрузка…</div>}
      {error && <div className="card" style={{ color: 'var(--red)' }}>{String((error as Error).message)}</div>}

      {data && (
        <>
          <div className="muted" style={{ marginBottom: 8 }}>
            Найдено: {data.total}
          </div>
          <table className="products">
            <thead>
              <tr>
                <th style={{ width: 60 }}></th>
                <th>Название</th>
                <th>Артикул / SKU</th>
                <th className="num">Цена</th>
                <th className="num">Маркет.</th>
                <th>Индекс</th>
                <th className="num" title="Минимальная цена на других площадках (Wildberries, Я.Маркет и др.)">Min внешн.</th>
                <th className="num" title="Минимальная цена среди других продавцов Ozon на этот же товар">Min Ozon</th>
                <th className="num" title="Минимальная цена по вашим же карточкам на других маркетплейсах">Min МП</th>
                <th className="num">Остаток</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => <ProductRow key={p.productId} p={p} />)}
              {data.items.length === 0 && (
                <tr><td colSpan={10} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                  Каталог пуст. Запустите синк товаров в разделе «Синхронизация».
                </td></tr>
              )}
            </tbody>
          </table>
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            <strong>Цены конкурентов:</strong> Ozon API не отдаёт сырые цены отдельных продавцов, только агрегированные минимумы по каналам. Колонка «Min внешн.» — цена конкурента вне Ozon (WB, ЯМ и др.), «Min Ozon» — минимум среди других продавцов на ту же карточку Ozon.
          </div>
        </>
      )}
    </>
  );
}

function ProductRow({ p }: { p: ProductRow }) {
  return (
    <tr>
      <td>{p.image ? <img src={p.image} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : null}</td>
      <td>
        <div style={{ fontWeight: 500 }}>{p.name}</div>
        <div className="muted" style={{ fontSize: 12 }}>{p.brand}</div>
      </td>
      <td>
        <div>{p.offerId}</div>
        <div className="muted" style={{ fontSize: 12 }}>{p.sku ?? '—'}</div>
      </td>
      <td className="num">
        {p.price ? `${p.price} ₽` : '—'}
        {p.oldPrice && <div className="muted" style={{ fontSize: 12, textDecoration: 'line-through' }}>{p.oldPrice} ₽</div>}
      </td>
      <td className="num">{p.marketingPrice ? `${p.marketingPrice} ₽` : '—'}</td>
      <td>
        {p.colorIndex ? (
          <>
            <span className={`color-dot ${p.colorIndex}`} />
            <span className="muted" style={{ fontSize: 12 }}>{p.colorIndex}</span>
          </>
        ) : <span className="muted">—</span>}
      </td>
      <td className="num">{fmtMin(p.extMinPrice, p.price)}</td>
      <td className="num">{fmtMin(p.ozonMinPrice, p.price)}</td>
      <td className="num">{fmtMin(p.mpMinPrice, p.price)}</td>
      <td className="num">{p.stockTotal}</td>
    </tr>
  );
}

function fmtMin(competitor: string | null, ours: string | null): React.ReactNode {
  if (!competitor || competitor === '0' || competitor === '0.00') return <span className="muted">—</span>;
  const c = Number(competitor);
  const o = ours ? Number(ours) : null;
  if (!Number.isFinite(c) || c <= 0) return <span className="muted">—</span>;
  const color = o == null ? undefined : c < o ? 'var(--red)' : c > o ? 'var(--green)' : undefined;
  return <span style={{ color }}>{c.toFixed(0)} ₽</span>;
}
